import React, { forwardRef } from 'react';
import styles from './JuryProjectCard.module.scss';
import useJuryStore from '../../../../store/juryStore';
import { useJuryFundingOptions } from '../../../../store/configStore';
import CardActionButton from './CardActionButton';
import { IconStar, IconStarFilled, IconCircleCheck, IconAward, IconAwardFilled } from '@tabler/icons-react';
import JuryProjectDetails from '../JuryProjectDetails/JuryProjectDetails';
import JuryCategoryBadge from '../../JuryCategoryBadge/JuryCategoryBadge';
import { mergeClassNames } from '../../../../shared/utility';

/**
 * @param project           Project data
 * @param isSelected        true when this card is the currently-selected card
 *                          in the list (highlights the border)
 * @param onSelect          called when the card body is clicked
 * @param showInlineDetails true on mobile, where the details accordion lives
 *                          inside the card itself. Desktop renders details
 *                          in a separate right-hand pane and passes false.
 */
const JuryProjectCard = forwardRef(({ id, project, isSelected = false, onSelect, showInlineDetails = false }, ref) => {
    const assignedFunding = useJuryStore((s) => s.poll[project._id] ?? null);
    const isAssignedFunding = assignedFunding !== null;
    const isBookmarked = useJuryStore((s) => s.bookmarks.has(project._id));
    const assignFunding = useJuryStore((s) => s.assignFunding);
    const toggleBookmark = useJuryStore((s) => s.toggleBookmark);
    const isConfirmed = useJuryStore((s) => s.isConfirmed);
    const fundingOptions = useJuryFundingOptions();
    const poll = useJuryStore((s) => s.poll);

    // Amounts assigned to OTHER projects
    const takenAmounts = Object.entries(poll)
        .filter(([pid]) => pid !== project._id)
        .map(([, amount]) => amount);

    // Track remaining available slots per amount
    const takenCountByAmount = takenAmounts.reduce((acc, amount) => {
        acc[amount] = (acc[amount] || 0) + 1;
        return acc;
    }, {});
    const usedCountByAmount = {};
    const optionsWithAvailability = fundingOptions.map((opt, idx) => {
        usedCountByAmount[opt.amount] = usedCountByAmount[opt.amount] || 0;
        const disabled = usedCountByAmount[opt.amount] < (takenCountByAmount[opt.amount] || 0);
        if (disabled) usedCountByAmount[opt.amount]++;
        return { idx, opt, disabled };
    });

    const thumbnail = project.projectGallery?.[0];

    const handleBookmark = () => toggleBookmark(project._id);

    const handleSelect = () => {
        if (onSelect) onSelect(project);
    };

    const handleSelectChange = (e) => {
        const val = e.target.value;
        if (val === '__remove__') {
            assignFunding(project._id, null);
        } else {
            assignFunding(project._id, val ? Number(val) : null);
        }
    };

    const cardClasses = mergeClassNames(styles.ProjectCard, isSelected ? styles.ProjectCardSelected : '');

    return (
        <div id={id} ref={ref} className={cardClasses}>
            <button type="button" className={styles.ProjectCardLink} aria-pressed={isSelected} onClick={handleSelect}>
                <div className={styles.CardTop}>
                    <div className={styles.ImageColumn}>
                        <div className={styles.ImageSection}>
                            {thumbnail ? (
                                <img
                                    src={thumbnail.url}
                                    alt={thumbnail.alt || project.contestantName}
                                    className={styles.ProjectImage}
                                />
                            ) : (
                                <div className={styles.NoImage} />
                            )}
                            <div className={styles.VoteBadge}>
                                <IconCircleCheck className={styles.VoteIcon} aria-hidden="true" />
                                {(project.voteCount || 0).toLocaleString('de-DE')} Stimmen
                            </div>
                        </div>
                        {project.voteCountFinale !== undefined && project.voteCountFinale !== null && (
                            <div className={styles.VoteBadgeFinale}>
                                <IconCircleCheck className={styles.VoteIcon} aria-hidden="true" />
                                {project.voteCountFinale.toLocaleString('de-DE')} im Finale
                            </div>
                        )}
                    </div>
                    <div className={styles.InstitutionSection}>
                        <h2 className={styles.InstitutionName}>{project.contestantName}</h2>
                    </div>
                </div>

                {project.projectTitle && (
                    <div className={styles.TitleSection}>
                        <p className={styles.ProjectTitle}>{project.projectTitle}</p>
                    </div>
                )}

                <ul className={styles.TagsSection} aria-label="Kategorien">
                    {[...(project.badges || [])]
                        .sort((a, b) => {
                            const groupCompare = a.group.localeCompare(b.group);
                            if (groupCompare !== 0) return groupCompare;
                            return a.value.localeCompare(b.value);
                        })
                        .map((badge) => (
                            <JuryCategoryBadge
                                key={badge._id}
                                component="li"
                                size="sm"
                                label={badge.value}
                                color={badge.color}
                            />
                        ))}
                </ul>
            </button>

            {showInlineDetails && (
                <button
                    type="button"
                    className={styles.ToggleDetailsButton}
                    onClick={handleSelect}
                    aria-expanded={isSelected}
                >
                    {isSelected ? 'Projektdetails ausblenden -' : 'Projektdetails anzeigen +'}
                </button>
            )}

            {showInlineDetails && isSelected && (
                <div className={styles.InlineDetails}>
                    <JuryProjectDetails project={project} />
                    <button type="button" className={styles.ToggleDetailsButtonBottom} onClick={handleSelect}>
                        Projektdetails ausblenden -
                    </button>
                </div>
            )}

            {/* Actions Section */}
            <div className={styles.ActionsSection}>
                <div className="input-group">
                    <span
                        className={`input-group-text pe-0 border-end-0 ${isConfirmed ? styles.PrizeSelectMedalDisabled : 'bg-white'}`}
                        aria-hidden="true"
                    >
                        {isAssignedFunding ? (
                            <IconAwardFilled size="1.625rem" className={styles.PrizeSelectMedalActive} />
                        ) : (
                            <IconAward size="1.625rem" className={styles.PrizeSelectMedalInactive} />
                        )}
                    </span>

                    <select
                        className={`form-select border-start-0 ${styles.PrizeSelect}`}
                        value={assignedFunding !== null ? String(assignedFunding) : ''}
                        disabled={isConfirmed}
                        aria-label="Förderung vergeben"
                        onChange={handleSelectChange}
                        id={`project-${project._id}-funding`}
                        name={`project-${project._id}-funding`}
                    >
                        <option value="" disabled>
                            Förderung vergeben…
                        </option>
                        <option value="__remove__" disabled={assignedFunding === null}>
                            Förderung entfernen
                        </option>
                        {optionsWithAvailability.map(({ idx, opt, disabled }) => (
                            <option key={idx} value={String(opt.amount)} disabled={disabled}>
                                {opt.displayValue}
                            </option>
                        ))}
                    </select>
                </div>

                <CardActionButton
                    icon={
                        isBookmarked ? (
                            <IconStarFilled size="1.5rem" className={styles.BookmarkIconActive} aria-hidden="true" />
                        ) : (
                            <IconStar size="1.5rem" className={styles.BookmarkIconInactive} aria-hidden="true" />
                        )
                    }
                    label="Merken"
                    onClick={handleBookmark}
                    ariaLabel={isBookmarked ? 'Von Merkliste entfernen' : 'Zur Merkliste hinzufügen'}
                    disabled={isConfirmed}
                />
            </div>
        </div>
    );
});

export default JuryProjectCard;
